import { useCallback, useEffect, useState } from 'react';
import { AppBar, Button, Box, Paper } from '@mui/material';
import Grid from '@mui/material/Grid2';
import { blueGrey } from '@mui/material/colors';
import { useNavigate } from 'react-router-dom';

import { Campaign } from '../../../../types/Campaign';
import { useAppSidebar } from '../../../../hooks/App/AppSidebar';
import { CampaignWizardContextValue } from '../../../../hooks/useCampaignWizard';

import AppLoader from '../../../../components/AppLoader';
import CampaignDuplicateValidator from '../CampaignDuplicateValidator';
import CampaignWizardCreativeStep from './CampaignWizardCreativeStep';
import CampaignWizardGeographyStep from './CampaignWizardGeographyStep';
import CampaignWizardReviewStep from './CampaignWizardReviewStep';
import CampaignWizardSetupStep from './CampaignWizardSetupStep';
import CampaignWizardStepNav from './CampaignWizardStepNav';
import Row from '../../../../components/Row';

export type CampaignWizardStepProps = {
    campaign: Campaign;
    onChange(value: Partial<Campaign>): void;
};

export type CampaignWizardProps = {
    campaignWizard: CampaignWizardContextValue;
};

export default function CampaignWizard(props: CampaignWizardProps) {
    const { campaignWizard } = props;

    const navigate = useNavigate();
    const { appSidebarOffset } = useAppSidebar();

    const {
        $campaignDuplicateValidator,
        $loader,

        campaign,
        fetchCampaign,
        handleSubmit,
        submitCampaign,
        ignoreDuplicates,
        isCompletedCampaign,
        isDraftCampaign,
        saveInProgress,
        setCampaign,
        setIgnoreDuplicates,
        stepper,
    } = campaignWizard;
    const { isLoading = false } = $loader || {};

    const handleCampaignChange = useCallback(
        (value: Partial<Campaign>) => {
            setCampaign((prev: Campaign) => {
                return { ...prev, ...value };
            });
        },
        [setCampaign]
    );

    const handleIgnoreCampaignDuplicates = () => {
        setIgnoreDuplicates(true);
    };

    const handleCancelCampaignWizard = (): void => {
        if (campaignWizard.campaign.campaignId) {
            navigate(`/campaign/${campaignWizard.campaign.campaignId}`);
        } else {
            navigate('/campaignManager');
        }
    };

    const handlePreviousStepCampaignWizard = (): void => {
        stepper.toPreviousStep();
    };

    const handleNextStepCampaignWizard = (): void => {
        stepper.toNextStep();
    };

    // Track which steps have been visited — mount a step only once visited,
    // then keep it mounted (display:none) so local state survives navigation.
    const [visitedSteps, setVisitedSteps] = useState<Set<number>>(new Set([0]));

    useEffect(() => {
        setVisitedSteps((prev) => new Set(prev).add(stepper.activeStep));
    }, [stepper.activeStep]);

    useEffect(() => {
        fetchCampaign();
    }, [fetchCampaign]);

    return (
        <form onSubmit={handleSubmit}>
            <AppLoader loading={isLoading} />
            <Box sx={{ flexGrow: 1 }}>
                <Grid container>
                    <Grid
                        size={{
                            sm: 2,
                            xs: 3,
                        }}
                    >
                        <AppBar
                            position="sticky"
                            color="transparent"
                            sx={{
                                backgroundColor: (theme) => theme.palette.common.white,
                                top: 64 + 1,
                            }}
                        >
                            <Paper elevation={0}>
                                <CampaignWizardStepNav nonLinear={campaign?.id ? true : false} stepper={stepper} />
                            </Paper>
                        </AppBar>
                    </Grid>
                    <Grid
                        sx={{
                            backgroundColor: blueGrey['50'],
                            borderWidth: 0,
                            borderLeftWidth: 1,
                            borderStyle: 'solid',
                            borderColor: (theme) => theme.palette.grey['300'],
                            p: 3,
                            minHeight: '100vh',
                        }}
                        size={{
                            sm: 10,
                            xs: 9,
                        }}
                    >
                        <Box>
                            {visitedSteps.has(0) && (
                                <Box sx={{ display: stepper.activeStep === 0 ? 'block' : 'none' }}>
                                    <CampaignWizardSetupStep campaign={campaign} onChange={handleCampaignChange} />
                                </Box>
                            )}
                            {visitedSteps.has(1) && (
                                <Box sx={{ display: stepper.activeStep === 1 ? 'block' : 'none' }}>
                                    <CampaignWizardGeographyStep campaign={campaign} onChange={handleCampaignChange} />
                                </Box>
                            )}
                            {visitedSteps.has(2) && (
                                <Box sx={{ display: stepper.activeStep === 2 ? 'block' : 'none' }}>
                                    <CampaignWizardCreativeStep campaign={campaign} onChange={handleCampaignChange} />
                                </Box>
                            )}
                            {visitedSteps.has(3) && (
                                <Box sx={{ display: stepper.activeStep === 3 ? 'block' : 'none' }}>
                                    <CampaignWizardReviewStep campaign={campaign} onChange={handleCampaignChange} />
                                </Box>
                            )}
                        </Box>
                    </Grid>

                    <Grid size={12}>
                        <AppBar
                            position="fixed"
                            color="transparent"
                            sx={{
                                backgroundColor: (theme) => theme.palette.common.white,
                                top: 'auto',
                                bottom: 0,
                                left: appSidebarOffset,
                                width: `calc(100% - ${appSidebarOffset}px)`,
                            }}
                        >
                            <Paper
                                elevation={0}
                                square={true}
                                sx={{
                                    borderWidth: '0px',
                                    borderTopWidth: '1px',
                                    borderStyle: 'solid',
                                    borderColor: (theme) => theme.palette.grey['300'],
                                    paddingX: 4,
                                    paddingY: 2,
                                }}
                            >
                                <Row alignItems="center" justifyContent="flex-end">
                                    {stepper.activeStep === 0 && (
                                        <Button variant="outlined" color="primary" onClick={handleCancelCampaignWizard}>
                                            Cancel
                                        </Button>
                                    )}

                                    {stepper.hasPreviousStep() && (
                                        <Button
                                            variant="outlined"
                                            color="primary"
                                            onClick={handlePreviousStepCampaignWizard}
                                        >
                                            Back
                                        </Button>
                                    )}

                                    {stepper.hasNextStep() && (
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            onClick={handleNextStepCampaignWizard}
                                        >
                                            Next
                                        </Button>
                                    )}

                                    {stepper.hasNextStep() === false && (
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            disabled={isCompletedCampaign || saveInProgress}
                                            onClick={() => submitCampaign()}
                                        >
                                            {isDraftCampaign ? 'Submit' : 'Update'}
                                        </Button>
                                    )}
                                </Row>
                            </Paper>
                        </AppBar>
                    </Grid>
                </Grid>

                {$campaignDuplicateValidator && (
                    <CampaignDuplicateValidator
                        campaignDuplicateValidator={$campaignDuplicateValidator}
                        campaign={campaign}
                        ignoreDuplicates={ignoreDuplicates}
                        onIgnoreDuplicates={handleIgnoreCampaignDuplicates}
                    />
                )}
            </Box>
        </form>
    );
}
